import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { PortfolioScorecard } from "@/components/PortfolioScorecard";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Layers, Upload, FileSpreadsheet, Save, Plug, RefreshCw, Loader2 } from "lucide-react";

const SAMPLE_JSON = `{
  "workstreams": [
    {
      "name": "Foundation",
      "items": [
        {"plan_id": "F1", "owner_user_id": "alice", "baseline_residual": 20, "target_residual": 8, "created_at": "2026-01-01T00:00:00Z", "due_date": "2026-06-01T00:00:00Z", "status": "in_progress", "progress_pct": 60, "title": "Pour concrete"},
        {"plan_id": "F2", "owner_user_id": "bob", "created_at": "2026-01-01T00:00:00Z", "due_date": "2026-04-01T00:00:00Z", "status": "in_progress", "progress_pct": 20, "title": "Install rebar"}
      ],
      "declared_dependencies": [{"from_plan_id": "F2", "to_plan_id": "F1", "kind": "fs"}]
    },
    {
      "name": "Walls",
      "items": [
        {"plan_id": "W1", "owner_user_id": "alice", "created_at": "2026-04-01T00:00:00Z", "due_date": "2026-09-01T00:00:00Z", "status": "in_progress", "progress_pct": 10, "title": "Erect framing"}
      ]
    }
  ]
}`;

export default function PlanPortfolio() {
  const [mode, setMode] = useState("json"); // "json" | "upload" | "connector"
  const [rawJson, setRawJson] = useState("");
  const [file, setFile] = useState(null);
  const [infer, setInfer] = useState(false);
  const [data, setData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastWorkstreams, setLastWorkstreams] = useState(null);
  const [connectors, setConnectors] = useState([]);
  const [connectorId, setConnectorId] = useState("");

  useEffect(() => {
    api.get("/connectors").then(({ data: r }) => {
      const ready = (r.items || []).filter((c) => (c.last_sync_count || 0) > 0);
      setConnectors(ready);
      if (ready.length > 0 && !connectorId) setConnectorId(ready[0].connector_id);
    }).catch(() => { /* admin-only — viewer roles see nothing */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitConnector = async () => {
    if (!connectorId) { toast.error("Pick a connector"); return; }
    setSubmitting(true);
    try {
      const { data: r } = await api.post("/plan-portfolio/score/from-connector", {
        connector_id: connectorId, infer_dependencies: infer,
      });
      setData(r);
      setLastWorkstreams(r.workstreams);
      toast.success(`Scored ${r.connector?.plan_count || 0} plans from ${r.connector?.name || "the connector"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Connector scoring failed");
    } finally { setSubmitting(false); }
  };

  const saveAsProgramme = async () => {
    if (!lastWorkstreams) return;
    const name = window.prompt("Programme name?");
    if (!name) return;
    setSaving(true);
    try {
      const { data: r } = await api.post("/programmes", {
        name, workstreams: lastWorkstreams,
        options: { infer_dependencies: infer },
      });
      toast.success(`Saved as programme "${r.name}" — tracking starts now`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const submitJson = async () => {
    setSubmitting(true);
    try {
      let parsed;
      try { parsed = JSON.parse(rawJson); }
      catch { toast.error("Invalid JSON"); setSubmitting(false); return; }
      const body = parsed.workstreams
        ? { workstreams: parsed.workstreams, options: { infer_dependencies: infer } }
        : { workstreams: parsed, options: { infer_dependencies: infer } };
      const { data: r } = await api.post("/plan-portfolio/score", body);
      setData(r);
      setLastWorkstreams(body.workstreams);
      toast.success(`Scored ${r.portfolio.total_items} items across ${r.portfolio.workstream_count} workstream(s)`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Portfolio scoring failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submitFile = async () => {
    if (!file) { toast.error("Choose a file first"); return; }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("infer_dependencies", String(infer));
      const { data: r } = await api.post("/plan-portfolio/score/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setData(r);
      toast.success(`Scored ${r.portfolio.total_items} items across ${r.portfolio.workstream_count} workstream(s)`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload scoring failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-violet-600 font-semibold flex items-center gap-2">
              <Layers size={14} /> EKSELENS · ERIO · Plan portfolio
            </p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight text-ek-ink">Plan Portfolio Integrity</h1>
            <p className="mt-1 text-sm text-ek-ink/65 max-w-3xl">
              Score multiple plans together. Get a programme-level rollup, surface failure modes that exist across all of them, and (opt-in) let ERIO suggest cross-plan dependencies you may not have declared.
            </p>
          </div>
        </div>

        {/* Input panel */}
        <div className="mt-6 rounded-xl bg-white p-5 ring-1 ring-slate-200">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-2" data-testid="pp-mode-tabs">
              <button onClick={() => setMode("json")} data-testid="pp-mode-json"
                className={`rounded-md px-3 py-1.5 text-sm font-medium ${mode === "json" ? "bg-ek-purple text-white" : "border border-slate-200 text-ek-ink/70 hover:bg-slate-50"}`}>
                Paste JSON
              </button>
              <button onClick={() => setMode("upload")} data-testid="pp-mode-upload"
                className={`rounded-md px-3 py-1.5 text-sm font-medium inline-flex items-center gap-1.5 ${mode === "upload" ? "bg-ek-purple text-white" : "border border-slate-200 text-ek-ink/70 hover:bg-slate-50"}`}>
                <Upload size={14} /> Upload CSV / Excel / JSON
              </button>
              <button onClick={() => setMode("connector")} data-testid="pp-mode-connector"
                className={`rounded-md px-3 py-1.5 text-sm font-medium inline-flex items-center gap-1.5 ${mode === "connector" ? "bg-ek-purple text-white" : "border border-slate-200 text-ek-ink/70 hover:bg-slate-50"}`}>
                <Plug size={14} /> Import from connector
              </button>
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="pp-infer-toggle">
              <Switch checked={infer} onCheckedChange={setInfer} data-testid="pp-infer-switch" />
              <span className="text-ek-ink/75">
                Would you like ERIO to identify cross-plan dependencies?
                <span className="ml-1 text-xs text-ek-ink/45">(Defensible AI — flagged as candidates)</span>
              </span>
            </label>
          </div>

          {mode === "json" ? (
            <div className="mt-4">
              <label className="text-sm font-medium text-ek-ink">Portfolio JSON</label>
              <textarea
                value={rawJson}
                onChange={(e) => setRawJson(e.target.value)}
                rows={12}
                placeholder={SAMPLE_JSON}
                data-testid="pp-json-input"
                className="mt-2 w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-800 focus:border-ek-purple focus:outline-none"
              />
              <div className="mt-2 flex items-center gap-2">
                <button onClick={() => setRawJson(SAMPLE_JSON)} className="text-xs text-violet-700 hover:underline" data-testid="pp-use-sample">
                  Use sample data
                </button>
                <span className="text-xs text-ek-ink/45">·</span>
                <button onClick={submitJson} disabled={submitting || !rawJson.trim()}
                  className="ml-auto rounded-md bg-ek-purple px-4 py-2 text-sm font-semibold text-white hover:bg-ek-purple/90 disabled:opacity-50"
                  data-testid="pp-submit-json">
                  {submitting ? "Scoring…" : "Score portfolio"}
                </button>
              </div>
            </div>
          ) : mode === "upload" ? (
            <div className="mt-4">
              <label className="text-sm font-medium text-ek-ink flex items-center gap-2">
                <FileSpreadsheet size={14} /> File (.csv / .xlsx / .json)
              </label>
              <p className="text-xs text-ek-ink/55 mt-1">
                Excel: one sheet per workstream (sheet name = workstream). CSV: add a <code>workstream</code> column to group, or upload one CSV per workstream.
              </p>
              <input
                type="file"
                accept=".csv,.xlsx,.xlsm,.json"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="pp-file-input"
                className="mt-3 block w-full text-sm text-ek-ink/70 file:mr-3 file:rounded-md file:border-0 file:bg-violet-100 file:px-4 file:py-2 file:text-sm file:font-medium file:text-violet-700 hover:file:bg-violet-200"
              />
              <button onClick={submitFile} disabled={submitting || !file}
                className="mt-4 rounded-md bg-ek-purple px-4 py-2 text-sm font-semibold text-white hover:bg-ek-purple/90 disabled:opacity-50"
                data-testid="pp-submit-upload">
                {submitting ? "Scoring…" : "Score uploaded file"}
              </button>
            </div>
          ) : (
            <div className="mt-4" data-testid="pp-connector-panel">
              <label className="text-sm font-medium text-ek-ink flex items-center gap-2">
                <Plug size={14} /> Pick a synced connector
              </label>
              <p className="text-xs text-ek-ink/55 mt-1">
                Only connectors with at least one successful sync are listed. Visit <a href="/connectors" className="text-violet-700 hover:underline">PPM connectors</a> to configure one, then come back.
              </p>
              {connectors.length === 0 ? (
                <div className="mt-3 rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-sm text-ek-ink/65"
                     data-testid="pp-connector-empty">
                  No synced connectors yet. <a href="/connectors" className="text-violet-700 hover:underline">Configure one →</a>
                </div>
              ) : (
                <>
                  <select value={connectorId}
                    onChange={(e) => setConnectorId(e.target.value)}
                    data-testid="pp-connector-select"
                    className="mt-3 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-ek-purple focus:outline-none">
                    {connectors.map((c) => (
                      <option key={c.connector_id} value={c.connector_id}>
                        {c.name} — {c.last_sync_count} plan{c.last_sync_count === 1 ? "" : "s"}, last sync {c.last_sync_at ? new Date(c.last_sync_at).toLocaleString() : "never"}
                      </option>
                    ))}
                  </select>
                  <button onClick={submitConnector} disabled={submitting || !connectorId}
                    className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-ek-purple px-4 py-2 text-sm font-semibold text-white hover:bg-ek-purple/90 disabled:opacity-50"
                    data-testid="pp-submit-connector">
                    {submitting ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    {submitting ? "Scoring…" : "Score connector plans"}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="mt-6">
          {data ? (
            <>
              <div className="mb-4 flex justify-end">
                <button onClick={saveAsProgramme} disabled={saving || !lastWorkstreams}
                        data-testid="pp-save-programme-btn"
                        className="inline-flex items-center gap-1.5 rounded-md bg-violet-700 px-3 py-2 text-sm font-semibold text-white hover:bg-violet-800 disabled:opacity-50">
                  <Save size={14} /> {saving ? "Saving…" : "Save as programme"}
                </button>
              </div>
              <PortfolioScorecard data={data} testidPrefix="pp" />
            </>
          ) : (
            <div className="rounded-xl bg-white p-12 text-center text-slate-500 ring-1 ring-slate-200" data-testid="pp-empty">
              Paste a portfolio JSON or upload a CSV/Excel/JSON file to see the programme-level rollup, systemic patterns, and cross-plan coherence checks.
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
